import { Icon, Spinner } from "@sarva/ui";
import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Brand } from "@/shared/layout/Brand";
import { readLaunchFragment } from "../api/launch";
import { useLaunch } from "../hooks/useLaunch";
import { SIGN_IN_ROUTE } from "./RequireAuth";

export const LAUNCH_ROUTE = "/launch";

/**
 * `/launch` — where a partner product's "Open in Sarva" button lands (the backend's D148).
 *
 * Public, outside `RequireAuth`: the visitor has no session yet, and this is what makes one.
 *
 * ── The order matters ───────────────────────────────────────────────────────
 *
 * 1. Read the assertion from the URL **fragment**, once.
 * 2. **Remove the fragment from history immediately**, before the request is even sent — a
 *    replace navigation, which under the browser router is `history.replaceState`. The
 *    assertion is single-use and lives for seconds, but a bearer credential sitting in the
 *    back-button history or a screenshot of the address bar is exactly what D148's 60-second
 *    rule exists to make worthless; there is no reason to leave it there even that long.
 * 3. Exchange it. On success go to `return_to` if it is a same-origin relative path, else the
 *    dashboard.
 *
 * (H) It runs once per mount, guarded by a ref. React's development double-invoke would
 * otherwise send the assertion twice, and the second request is a replay the API refuses —
 * which would paint "already used" over a launch that had just succeeded.
 *
 * ── One failure state, on purpose ───────────────────────────────────────────
 *
 * The API answers every refusal with the same `401` (an expired link, a replay, an unknown
 * user, a forged signature are indistinguishable by design), so this screen has one thing to
 * say and says it in the person's terms. No retry button: the assertion was spent by the
 * attempt, and the only way forward is a fresh one from the clinic system — or ordinary
 * sign-in, which is linked.
 */
export function LaunchPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const launch = useLaunch();
  const started = useRef(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;

    const { assertion, returnTo } = readLaunchFragment(location.hash);
    // (2) above: the fragment goes before anything else happens.
    void navigate(
      { pathname: location.pathname, search: location.search },
      { replace: true },
    );

    if (assertion === "") {
      setFailed(true);
      return;
    }
    launch.mutate(assertion, {
      onSuccess: () => {
        void navigate(returnTo, { replace: true });
      },
      onError: () => setFailed(true),
    });
    // Deliberately once: the inputs are read on arrival and the fragment is then gone.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <main className="flex min-h-dvh flex-col items-center justify-center bg-canvas px-6 pb-28 pt-12">
      <div className="w-full max-w-[27rem] rounded-xl border border-default bg-default p-9 text-center shadow-card sm:p-11">
        <Brand className="mx-auto mb-6 h-8" decorative />
        {failed ? (
          <>
            <h1 className="text-2xl font-bold tracking-tight text-default">
              This link can’t be used
            </h1>
            <p
              className="mt-4 rounded-sm border border-warning bg-warning-weak px-3 py-2 text-left text-sm text-warning"
              role="alert"
            >
              This link has expired or was already used. Open Sarva again from
              your clinic system.
            </p>
            <Link
              to={SIGN_IN_ROUTE}
              className="mt-6 inline-flex items-center gap-1.5 text-sm font-medium text-primary underline-offset-2 hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-strong"
            >
              Sign in with your Sarva account instead
              <Icon name="arrow-right" size={4} />
            </Link>
          </>
        ) : (
          <>
            <h1 className="text-2xl font-bold tracking-tight text-default">
              Opening Sarva
            </h1>
            <div className="mt-6 flex justify-center" role="status">
              <Spinner label="Opening Sarva from your clinic system" size={5} />
            </div>
          </>
        )}
      </div>
    </main>
  );
}
